import { FiltersExpression, Operation, Operator } from "types";
import { InternalResourceConfig } from "./types";

export function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== "object") return undefined;

  const keys = path.split(".");
  let value: unknown = obj;

  for (const key of keys) {
    if (value === null || value === undefined) return undefined;
    if (typeof value !== "object") return undefined;
    value = (value as Record<string, unknown>)[key];
  }

  return value;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function buildFieldFilters(
  config: InternalResourceConfig,
  urlFilters: Record<string, string>,
) {
  return (config.list.filters ?? [])
    .map((filterConfig) => ({
      filterConfig,
      value: urlFilters[filterConfig.key]?.trim(),
    }))
    .filter((entry) => !!entry.value)
    .map(({ filterConfig, value }) => {
      if (filterConfig.partial && !UUID_PATTERN.test(value)) {
        return {
          op: filterConfig.partial.op ?? Operation.Ilike,
          field: filterConfig.partial.field,
          values: [value],
        };
      }

      return {
        op: filterConfig.op ?? Operation.Eq,
        field: filterConfig.field,
        values: [value],
      };
    });
}

export function buildGraphQLFilters(
  config: InternalResourceConfig,
  urlFilters: Record<string, string>,
): FiltersExpression | undefined {
  const fieldFilters = buildFieldFilters(config, urlFilters);
  const searchExpression = buildSearchExpression(config, urlFilters);

  if (fieldFilters.length === 0) return searchExpression;

  return {
    operator: Operator.And,
    filters: fieldFilters,
    childExpressions: searchExpression ? [searchExpression] : [],
  };
}

function buildSearchExpression(
  config: InternalResourceConfig,
  urlFilters: Record<string, string>,
): FiltersExpression | undefined {
  const search = urlFilters.search?.trim();
  if (!search) return undefined;

  const searchFields = config.list.searchFields;
  if (searchFields.length === 0) return undefined;

  const isUuid = UUID_PATTERN.test(search);

  const filters = searchFields
    .map((entry) =>
      typeof entry === "string" ? { field: entry, op: Operation.Ilike } : entry,
    )
    .filter((entry) => isUuid || !entry.field.endsWith(".id"))
    .map((fieldConfig) => {
      return {
        op: fieldConfig.op,
        field: fieldConfig.field,
        values: [search],
      };
    });

  if (filters.length === 0) return undefined;

  if (filters.length === 1) {
    return {
      operator: Operator.And,
      filters,
      childExpressions: [],
    };
  }

  return {
    operator: Operator.Or,
    filters,
    childExpressions: [],
  };
}
